const MAX_CHARS = 1500;

const clip = (text, n = 140) => {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

function chartLine(el) {
  const data = el.data || {};
  const title = clip(el.title || "Untitled chart", 60);
  if (el.chartType === "table") {
    return `Table "${title}" with columns ${(data.columns || []).map((c) => clip(c, 20)).join(", ")}`;
  }
  if (el.chartType === "timeline") {
    const steps = (data.events || []).map((e) => clip(e.label, 30)).join(" → ");
    return `Timeline "${title}": ${steps}`;
  }
  const categories = data.categories || [];
  const series = data.series || [];
  const values = series.length === 1
    ? categories.map((c, i) => `${clip(c, 20)} ${series[0].values?.[i] ?? ""}`.trim()).join(", ")
    : `${series.map((s) => clip(s.name, 20)).join(" and ")} across ${categories.map((c) => clip(c, 20)).join(", ")}`;
  return `${el.chartType || "bar"} chart "${title}": ${values}`;
}

function lineFor(el) {
  switch (el.type) {
    case "text":
      return el.content?.trim() ? `Text: "${clip(el.content)}"` : null;
    case "sticky":
      return el.content?.trim() ? `Sticky note: "${clip(el.content)}"` : null;
    case "lumen_card":
      return el.content?.trim() ? `Lumen card: "${clip(el.content)}"` : null;
    case "chart":
      return chartLine(el);
    case "artboard":
      return `Artboard "${clip(el.name || "Artboard", 60)}"`;
    case "image_slot": {
      if (!el.prompt) return null;
      const caption = (el.captions || [])[el.index || 0];
      if (caption) return `Generated image. What it shows: ${clip(caption, 240)}`;
      const detail = (el.details || [])[el.index || 0];
      if (detail) return `Generated image: ${clip(detail, 320)}`;
      return el.busy
        ? `Image being generated from the prompt "${clip(el.prompt)}"`
        : `Empty image slot for the prompt "${clip(el.prompt)}"`;
    }
    case "image":
      if (el.caption) return `${el.prompt || el.detail ? "Generated image" : "Image"}. What it shows: ${clip(el.caption, 240)}`;
      if (el.detail) return `Generated image: ${clip(el.detail, 320)}`;
      if (el.prompt) return `Generated image of "${clip(el.prompt)}"`;
      if (el.attribution?.source === "Unsplash") {
        return `Reference photo: ${clip(el.alt || "a photo", 100)} (by ${el.attribution.photographer} on Unsplash)`;
      }
      return "An uploaded image";
    default:
      return null;
  }
}

const FRAMEWORK_NAMES = {
  mindmap: "Mind map", swot: "SWOT", "pros-cons": "Pros and cons", kanban: "Kanban",
  fishbone: "Fishbone", "5why": "5 whys", brief: "Brief", smart: "SMART goal", affinity: "Affinity map",
};

function lumenLine(id, els) {
  const framework = els.find((e) => e.lumenFramework)?.lumenFramework;
  if (framework) {
    const words = els
      .filter((e) => (e.type === "text" || e.type === "sticky") && String(e.content || "").trim())
      .map((e) => `"${clip(e.content, 60)}"`);
    const who = els.some((e) => e.fromToolbar) ? "added from the toolbar" : "added by Lumen";
    return `${FRAMEWORK_NAMES[framework] || framework} ${who} [id: ${id}]: ${words.join(", ")}`;
  }
  const card = els.find((e) => e.type === "lumen_card");
  if (!card) return null;
  return `${card.cardType || "idea"} card added by Lumen [id: ${id}]: "${clip(card.content)}"`;
}

export function describeBoard(elements = []) {
  if (!elements.length) return "empty";

  const lines = [];
  let strokes = 0;
  let shapes = 0;
  let connectors = 0;
  const fromLumen = new Map();

  for (const el of elements) {
    if (el.lumenSource) {
      if (!fromLumen.has(el.lumenSource)) fromLumen.set(el.lumenSource, []);
      fromLumen.get(el.lumenSource).push(el);
      continue;
    }
  }
  for (const [id, els] of fromLumen) {
    const line = lumenLine(id, els);
    if (line) lines.push(`- ${line}`);
  }

  for (const el of elements) {
    if (el.lumenSource) continue;
    if (el.type === "stroke") { strokes += 1; continue; }
    if (el.type === "connector") { connectors += 1; continue; }
    if (["rect", "circle", "triangle", "line", "arrow"].includes(el.type)) { shapes += 1; continue; }
    const line = lineFor(el);
    if (line) lines.push(`- ${line}`);
  }

  const drawn = [
    strokes && `${strokes} freehand stroke${strokes === 1 ? "" : "s"}`,
    shapes && `${shapes} shape${shapes === 1 ? "" : "s"}`,
    connectors && `${connectors} connector${connectors === 1 ? "" : "s"}`,
  ].filter(Boolean);
  if (drawn.length) lines.push(`- Drawn by hand: ${drawn.join(", ")}`);

  let out = "";
  for (const line of lines) {
    if (out.length + line.length + 1 > MAX_CHARS) {
      out += "\n- …and more";
      break;
    }
    out += (out ? "\n" : "") + line;
  }
  return out || "empty";
}
